import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, DotsThreeVertical, MapPin, Pause, Play, Plus, ShoppingBag, Trash, Warning, Camera, Clock, Lifebuoy, MagicWand, Sparkle, Images, Star, Lightbulb, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import StatusPill from "@/components/StatusPill";
import AddUpdateSheet from "@/components/AddUpdateSheet";
import HelpSheet from "@/components/HelpSheet";
import MilestoneCelebrationSheet from "@/components/MilestoneCelebrationSheet";
import InlineEncouragement from "@/components/InlineEncouragement";
import PurchaseRecCard from "@/components/PurchaseRecCard";
import StepExtrasPanel from "@/components/StepExtrasPanel";

// localStorage key for the per-user "I've seen the first-project tour" flag.
// We tie it to the userId-free namespace because the auth context isn't
// imported here and onboarding is a soft hint, not a security-sensitive flag.
const ONBOARDING_SEEN_KEY = "halfjob_onboarding_seen";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [helpRecs, setHelpRecs] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMode, setHelpMode] = useState("need_help");
  const [helpStepId, setHelpStepId] = useState(null);
  const [tab, setTab] = useState("steps");
  const [tabAutoSet, setTabAutoSet] = useState(false);
  // First-time onboarding banner. Hidden once the user dismisses it (persisted
  // to localStorage) so returning users never see it again.
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem(ONBOARDING_SEEN_KEY),
  );
  const dismissOnboarding = () => {
    localStorage.setItem(ONBOARDING_SEEN_KEY, "1");
    setShowOnboarding(false);
  };

  const load = async () => {
    try {
      const [p, u, h] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get(`/projects/${id}/updates`),
        api.get(`/projects/${id}/help`),
      ]);
      setProject(p.data);
      setUpdates(u.data);
      setHelpRecs(h.data);
    } catch {
      toast.error("Project not found");
      navigate("/", { replace: true });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // On first successful load, when state is `awaiting_materials`, switch the
  // default tab from Steps → Materials so the user is nudged to acquire what's
  // missing before starting work. Only runs once per page load so it doesn't
  // fight the user if they manually flip to Steps.
  useEffect(() => {
    if (!project || tabAutoSet) return;
    if (project.state === "awaiting_materials") {
      setTab("materials");
    }
    setTabAutoSet(true);
  }, [project, tabAutoSet]);

  if (!project) {
    return <div className="p-6 text-timber-700">Loading…</div>;
  }

  const cover = fileUrl(project.cover_photo_id);
  const setStepStatus = async (stepId, status) => {
    await api.patch(`/projects/steps/${stepId}`, { status });
    load();
  };
  const setMaterialStatus = async (mId, status) => {
    await api.patch(`/projects/materials/${mId}`, { status });
    load();
  };
  const setProjectStatus = async (status) => {
    await api.patch(`/projects/${id}`, { status });
    toast.success(`Project ${status}`);
    load();
  };
  const remove = () => {
    // 5-second soft-delete with Undo. The actual API DELETE is deferred so the
    // user can recover from a mis-tap. We navigate away immediately so the
    // project disappears from the UI; the row stays in Mongo until the timer
    // fires, so Undo simply means "open the project again — it's still there."
    const projectId = id;
    const timeoutId = setTimeout(async () => {
      try {
        await api.delete(`/projects/${projectId}`);
      } catch {
        // Best-effort — surface a quiet error if it really fails.
        toast.error("Couldn't finish deleting that project. Try again.");
      }
    }, 5000);
    navigate("/", { replace: true });
    toast("Project deleted", {
      description: "We'll wipe everything in 5 seconds. Tap Undo to keep it.",
      duration: 5000,
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timeoutId);
          toast.success("Project restored");
          navigate(`/projects/${projectId}`, { replace: true });
        },
      },
    });
  };

  return (
    <div className="min-h-screen pb-40" data-testid="project-detail-page">
      <div className="relative">
        {cover ? (
          <div className="h-44 bg-kraft overflow-hidden">
            <img src={cover} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="h-24 bg-kraft" />
        )}
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <button
            onClick={() => navigate(-1)}
            data-testid="detail-back-btn"
            className="bg-chalk/95 backdrop-blur p-2 rounded-full border-2 border-stone_border shadow-tactile"
          >
            <ArrowLeft size={18} weight="bold" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="detail-menu-btn"
                className="bg-chalk/95 backdrop-blur p-2 rounded-full border-2 border-stone_border shadow-tactile"
              >
                <DotsThreeVertical size={18} weight="bold" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-chalk border-2 border-stone_border">
              {project.status !== "paused" ? (
                <DropdownMenuItem onClick={() => setProjectStatus("paused")} data-testid="action-pause">
                  <Pause size={16} className="mr-2" /> Pause project
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => setProjectStatus("active")} data-testid="action-resume">
                  <Play size={16} className="mr-2" /> Resume project
                </DropdownMenuItem>
              )}
              {project.status !== "done" && (
                <DropdownMenuItem onClick={() => setProjectStatus("done")} data-testid="action-mark-done">
                  <Check size={16} className="mr-2" /> Mark complete
                </DropdownMenuItem>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-block font-bold" data-testid="action-delete">
                    <Trash size={16} className="mr-2" /> Delete project
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-chalk border-2 border-stone_border">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove the project, steps, materials, history, notes and photos. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={remove} className="bg-block text-white hover:bg-block/90" data-testid="delete-confirm">Delete project</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="px-5 max-w-md mx-auto w-full -mt-6 space-y-4">
        {/* Immediate full-viewport celebration overlay. Replaces the old inline
            MilestoneBanner which could be hidden behind the cover photo or
            scrolled past. Always floats above page content with backdrop. */}
        <MilestoneCelebrationSheet
          projectId={id}
          milestone={project.pending_milestone}
          onDismissed={() => {
            load();
            // Optional UX: if the dismissed milestone was the completion
            // moment, scroll the CompletionCard into view so the user lands
            // on the next action instead of midway through done checkboxes.
            if (project.pending_milestone?.key === "p100") {
              setTimeout(() => {
                const el = document.querySelector('[data-testid="completion-card"]');
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 350);
            }
          }}
        />
        {showOnboarding && (
          <div
            data-testid="onboarding-callout"
            className="bg-chalk border-2 border-denim rounded-2xl p-4 shadow-tactile relative"
          >
            <button
              type="button"
              onClick={dismissOnboarding}
              data-testid="onboarding-dismiss"
              aria-label="Dismiss"
              className="absolute top-2 right-2 text-timber-500 hover:text-timber-900 p-1"
            >
              <X size={16} weight="bold" />
            </button>
            <div className="flex items-center gap-2">
              <Lightbulb size={18} weight="duotone" className="text-denim" />
              <p className="label-cap text-denim">First time here?</p>
            </div>
            <p className="font-heading text-base font-bold text-timber-900 mt-1 leading-snug">
              {`Here's how Half Job helps you finish this one.`}
            </p>
            <ul className="mt-2 space-y-1.5 text-sm text-timber-700 leading-snug">
              <li className="flex items-start gap-2">
                <Lifebuoy size={16} weight="duotone" className="text-denim flex-shrink-0 mt-0.5" />
                <span><strong className="text-timber-900">Need help</strong>{` — ask the AI anything about the current step. It knows what you've done so far.`}</span>
              </li>
              <li className="flex items-start gap-2">
                <Camera size={16} weight="duotone" className="text-masking flex-shrink-0 mt-0.5" />
                <span><strong className="text-timber-900">Check my work</strong> — snap a photo mid-step for a vision-based sanity check. At 100% it becomes <strong className="text-timber-900">Final check</strong>.</span>
              </li>
              <li className="flex items-start gap-2">
                <Check size={16} weight="bold" className="text-ok flex-shrink-0 mt-0.5" />
                <span>{`Tap the circle next to any step to mark it done. We'll celebrate the milestones.`}</span>
              </li>
            </ul>
            <button
              type="button"
              onClick={dismissOnboarding}
              data-testid="onboarding-got-it"
              className="mt-3 text-xs font-bold text-denim underline underline-offset-4"
            >
              Got it
            </button>
          </div>
        )}
        <div className="tactile-card p-5">
          <div className="flex items-center gap-2 flex-wrap" data-testid="project-status-row">
            <StatusPill status={project.state || project.bucket} data-testid="project-state-pill" />
          </div>
          <h1 className="font-heading text-2xl font-bold mt-2 text-timber-900" data-testid="detail-title">
            {project.title}
          </h1>
          {project.location && (
            <p className="text-sm text-timber-700 flex items-center gap-1 mt-0.5">
              <MapPin size={14} /> {project.location}
            </p>
          )}
          {project.description && (
            <p className="text-sm text-timber-700 mt-2">{project.description}</p>
          )}

          <div className="mt-4">
            <div className="flex items-center justify-between text-xs font-mono text-timber-700">
              <span>{project.steps_done} of {project.step_count} done</span>
              <span>{project.progress_pct}%</span>
            </div>
            <div className="mt-1 h-2 bg-kraft rounded-full overflow-hidden border border-stone_border">
              <div className="h-full bg-masking" style={{ width: `${project.progress_pct}%` }} />
            </div>
          </div>

          {project.ai_meta?.safety_warnings?.length > 0 && (
            <div className="mt-3 text-xs bg-[#FBEDEE] border border-block rounded-lg p-2 text-timber-900 flex items-start gap-2">
              <Warning size={14} weight="fill" className="text-block flex-shrink-0 mt-0.5" />
              <span>{project.ai_meta.safety_warnings.join(" · ")}</span>
            </div>
          )}
        </div>

        <ProjectGallery project={project} onChange={load} />

        <Button
          data-testid="add-update-btn"
          onClick={() => setSheetOpen(true)}
          className="w-full mt-4 bg-denim text-chalk hover:bg-denim-600 min-h-[60px] text-base font-bold btn-press"
        >
          <Warning size={20} weight="bold" className="mr-2" />
          Add update / problem
        </Button>

        {(() => {
          // Pick the best step id to anchor the help call to:
          // - prefer the user's current step
          // - fall back to the latest done step so Check My Work still works after a 100% project
          // - then fall back to the first step
          const steps = project.steps || [];
          const doneSteps = steps.filter((s) => s.status === "done");
          const latestDone = doneSteps[doneSteps.length - 1];
          const anchorStepId =
            project.current_step?.id || latestDone?.id || steps[0]?.id || null;
          const hasNonDoneStep =
            !!project.current_step && project.status !== "done";
          const isComplete =
            project.state === "complete" || project.status === "done";
          if (!anchorStepId) return null;
          return (
            <div className="grid grid-cols-2 gap-2 mt-3" data-testid="copilot-actions">
              {hasNonDoneStep ? (
                <Button
                  data-testid="need-help-btn"
                  variant="outline"
                  onClick={() => { setHelpStepId(anchorStepId); setHelpMode("need_help"); setHelpOpen(true); }}
                  className="bg-chalk border-2 border-stone_border text-timber-900 hover:border-masking min-h-[56px] font-bold"
                >
                  <Lifebuoy size={18} weight="duotone" className="mr-1" /> Need help
                </Button>
              ) : (
                // Keep the grid 2-cols balanced so Check My Work doesn't stretch oddly
                <div />
              )}
              <Button
                data-testid="check-my-work-btn"
                variant="outline"
                onClick={() => { setHelpStepId(anchorStepId); setHelpMode("check_my_work"); setHelpOpen(true); }}
                className={`bg-chalk border-2 border-stone_border text-timber-900 hover:border-masking min-h-[56px] font-bold ${isComplete ? "border-ok text-ok hover:border-ok hover:text-ok" : ""}`}
              >
                <Camera size={18} weight="duotone" className="mr-1" />
                {isComplete ? "Final check" : "Check my work"}
              </Button>
            </div>
          );
        })()}

        <Tabs value={tab} onValueChange={setTab} className="mt-5">
          <TabsList className="bg-kraft border-2 border-stone_border w-full p-1 h-12">
            <TabsTrigger value="materials" data-testid="tab-materials" className="flex-1 data-[state=active]:bg-chalk data-[state=active]:shadow-none font-bold">Materials</TabsTrigger>
            <TabsTrigger value="steps" data-testid="tab-steps" className="flex-1 data-[state=active]:bg-chalk data-[state=active]:shadow-none font-bold">Steps</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history" className="flex-1 data-[state=active]:bg-chalk data-[state=active]:shadow-none font-bold">History</TabsTrigger>
          </TabsList>

          <TabsContent value="steps" className="mt-4">
            {project.progress_pct === 100 && (
              <CompletionCard
                projectId={project.id}
                onFinalCheck={() => { setHelpStepId(null); setHelpMode("check_my_work"); setHelpOpen(true); }}
                onBackHome={() => navigate("/")}
              />
            )}
            <StepList project={project} onChange={load} setStepStatus={setStepStatus} />
          </TabsContent>
          <TabsContent value="materials" className="mt-4">
            <MaterialList
              project={project}
              setMaterialStatus={setMaterialStatus}
              onChange={load}
              navigate={navigate}
              onCheckShoppingList={() => { setHelpMode("check_shopping_list"); setHelpOpen(true); }}
            />
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <HistoryTimeline updates={updates} helpRecs={helpRecs} project={project} />
          </TabsContent>
        </Tabs>
      </div>

      <AddUpdateSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        projectId={id}
        steps={project.steps}
        onSaved={() => load()}
      />
      <HelpSheet
        open={helpOpen}
        onOpenChange={setHelpOpen}
        projectId={id}
        stepId={helpStepId || project.current_step?.id}
        mode={helpMode}
        project={project}
        onApplied={() => load()}
      />
    </div>
  );
}

function ProjectGallery({ project, onChange }) {
  const PHOTO_LIMIT = 10;
  const photoIds = project.photo_ids || [];
  const coverId = project.cover_photo_id;
  // De-dupe just in case cover is also in photo_ids
  const all = Array.from(new Set(coverId ? [coverId, ...photoIds.filter((p) => p !== coverId)] : photoIds));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const remaining = Math.max(0, PHOTO_LIMIT - all.length);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image too large (12MB max)");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      setUploading(true);
      const r = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/projects/${project.id}/photos`, { photo_id: r.data.id });
      toast.success("Photo added");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setAsCover = async (photoId) => {
    if (photoId === coverId) return;
    try {
      await api.patch(`/projects/${project.id}`, { cover_photo_id: photoId });
      toast.success("Cover updated");
      onChange();
    } catch {
      toast.error("Couldn't set cover");
    }
  };

  if (all.length === 0 && remaining === 0) return null;

  return (
    <div className="mt-4" data-testid="project-gallery">
      <div className="flex items-center justify-between mb-2">
        <p className="label-cap flex items-center gap-1">
          <Images size={14} weight="duotone" /> Photos · {all.length}/{PHOTO_LIMIT}
        </p>
        <p className="text-[11px] text-timber-500">Tap to set as cover</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {all.map((pid) => (
          <button
            key={pid}
            data-testid={`gallery-photo-${pid}`}
            onClick={() => setAsCover(pid)}
            className={`relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 snap-start btn-press ${
              pid === coverId ? "border-masking shadow-tactile-orange" : "border-stone_border"
            }`}
          >
            <img src={fileUrl(pid)} alt="" className="w-full h-full object-cover" />
            {pid === coverId && (
              <span className="absolute top-1 left-1 bg-masking text-timber-900 text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                <Star size={10} weight="fill" /> Cover
              </span>
            )}
          </button>
        ))}
        {remaining > 0 && (
          <button
            type="button"
            data-testid="gallery-add-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-stone_border bg-kraft flex flex-col items-center justify-center gap-1 text-timber-700 hover:border-masking hover:text-masking transition snap-start"
          >
            <Camera size={22} weight="duotone" />
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {uploading ? "Saving…" : "Add"}
            </span>
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        onChange={upload}
        className="hidden"
        data-testid="gallery-photo-input"
      />
    </div>
  );
}

/**
 * CompletionCard — surfaced at the top of the Steps tab once a project hits
 * 100%. Purpose: turn the end of a project into a memorable Half Job moment
 * rather than dropping the user back into a flat list of done checkboxes.
 *
 * - Primary action: Final Check (re-runs the existing HelpSheet in
 *   `check_my_work` mode, which already swaps its own copy to "Final check"
 *   at 100% — see HelpSheet for the existing functionality, unchanged).
 * - Secondary action: Back Home → closes the project loop and returns the
 *   user to the dashboard, the source of "Finish what you started."
 *
 * No badges, no points, no streaks, no leaderboards. Just Half Job voice.
 */
function CompletionCard({ onFinalCheck, onBackHome, projectId }) {
  const [outcome, setOutcome] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/outcome`);
        if (!cancelled) setOutcome(r.data?.outcome || null);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const submitOutcome = async () => {
    if (!rating) return;
    try {
      setSubmitting(true);
      await api.post(`/projects/${projectId}/outcome`, { rating, feedback });
      setOutcome({ rating, feedback });
      toast.success("Thanks for the review!");
    } catch (e) {
      toast.error("Couldn't save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="completion-card"
      className="rounded-2xl border-2 border-ok bg-chalk shadow-tactile mb-4 overflow-hidden"
    >
      <div className="h-2 w-full bg-ok" />
      <div className="p-5">
        <div className="flex items-center gap-2">
          <span className="text-xl" aria-hidden="true">🏁</span>
          <p className="label-cap text-ok">Project complete</p>
        </div>
        <h2 className="font-heading text-3xl font-bold text-timber-900 mt-2 leading-tight">
          Yep. I did that.
        </h2>
        <p className="text-timber-700 text-sm mt-3 leading-relaxed">
          Most Half Jobs never get this far.
        </p>
        <p className="text-timber-700 text-sm leading-relaxed">
          Take a minute and enjoy it.
        </p>

        {/* Outcome rating — collapsed once submitted, kept inline so we don't yank
            the user away from the celebration. */}
        <div className="mt-5 border-t border-stone_border pt-4" data-testid="outcome-card">
          {outcome ? (
            <p className="text-sm text-timber-700">
              <span className="font-bold text-timber-900">You rated this {outcome.rating}/5.</span>
              {outcome.feedback ? ` "${outcome.feedback}"` : " Thanks for the review."}
            </p>
          ) : (
            <>
              <p className="text-sm font-bold text-timber-900 mb-2">How did Half Job help?</p>
              <div className="flex gap-1.5 mb-2" data-testid="outcome-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`outcome-star-${n}`}
                    onClick={() => setRating(n)}
                    className={`w-9 h-9 rounded-full border-2 font-bold text-lg ${rating >= n ? "bg-masking border-timber-900 text-timber-900" : "bg-chalk border-stone_border text-timber-500"}`}
                  >★</button>
                ))}
              </div>
              <textarea
                data-testid="outcome-feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Optional — what worked, what didn't?"
                rows={2}
                className="w-full bg-chalk border-2 border-stone_border rounded-lg p-2 text-sm mb-2"
              />
              <Button
                data-testid="outcome-submit-btn"
                onClick={submitOutcome}
                disabled={!rating || submitting}
                className="w-full bg-timber-900 text-masking hover:bg-timber-900 min-h-[44px] font-bold disabled:opacity-50"
              >
                {submitting ? "Saving…" : "Share review"}
              </Button>
            </>
          )}
        </div>

        <div className="mt-5 space-y-3">
          <Button
            data-testid="completion-final-check-btn"
            onClick={onFinalCheck}
            className="w-full bg-ok text-white hover:bg-ok/90 min-h-[56px] text-base font-bold btn-press"
          >
            Final check
          </Button>
          <p className="text-xs text-timber-700 text-center -mt-1">
            Get one last review before you put the tools away.
          </p>
          <Button
            data-testid="completion-back-home-btn"
            onClick={onBackHome}
            variant="outline"
            className="w-full bg-chalk border-2 border-timber-900 text-timber-900 hover:bg-timber-900 hover:text-chalk min-h-[56px] text-base font-bold btn-press"
          >
            Back home
          </Button>
          <p className="text-xs text-timber-700 text-center -mt-1">
            Ready for another Half Job?
          </p>
        </div>
      </div>
    </div>
  );
}



function StepList({ project, onChange, setStepStatus }) {  const [newTitle, setNewTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const addStep = async () => {
    if (!newTitle.trim()) return;
    try {
      setBusy(true);
      await api.post(`/projects/${project.id}/steps`, { title: newTitle.trim() });
      setNewTitle("");
      onChange();
    } finally {
      setBusy(false);
    }
  };

  // The inline encouragement card sits directly above the current step in
  // the steps stream. If the project is fully complete (no current step), we
  // render it at the very top of the list so the 100% copy still has a home.
  const currentIdx = (project.steps || []).findIndex((s) => s.status === "current");
  const encouragementAt = currentIdx >= 0 ? currentIdx : 0;

  return (
    <div className="space-y-2" data-testid="steps-list">
      {project.steps?.length === 0 && (
        <p className="text-sm text-timber-500">No steps yet. Add the first action below.</p>
      )}
      {(project.steps || []).map((s, i) => (
        <Fragment key={s.id}>
          {i === encouragementAt && (
            <InlineEncouragement
              progressPct={project.progress_pct || 0}
              stepsDone={project.steps_done || 0}
            />
          )}
        <div
          data-testid={`step-row-${s.id}`}
          className={`tactile-card p-3 ${
            s.status === "current" ? "border-masking shadow-tactile-orange" : ""
          } ${s.status === "blocked" ? "border-block bg-[#FBEDEE]" : ""}`}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => setStepStatus(s.id, s.status === "done" ? "current" : "done")}
              data-testid={`step-toggle-${s.id}`}
              className={`w-9 h-9 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition ${
                s.status === "done"
                  ? "bg-ok border-ok text-white"
                  : "bg-chalk border-stone_border hover:border-masking"
              }`}
            >
              {s.status === "done" && <Check size={18} weight="bold" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${s.status === "done" ? "line-through text-timber-500" : "text-timber-900"}`}>
                {s.title}
              </p>
              {s.notes && <p className="text-xs text-timber-700 truncate">{s.notes}</p>}
            </div>
            <StatusPill status={s.status} />
          </div>
          <StepExtrasPanel step={s} />
        </div>
        </Fragment>
      ))}
      {/* Completed projects shouldn't encourage users to extend the finished plan. */}
      {(project.progress_pct || 0) < 100 && (
      <div className="flex gap-2 pt-2">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addStep()}
          placeholder="Add another step…"
          data-testid="step-new-input"
          className="min-h-[52px] bg-chalk border-2 border-stone_border rounded-xl text-base"
        />
        <Button onClick={addStep} disabled={busy} className="bg-timber-900 text-masking min-h-[52px]" data-testid="step-add-btn">
          <Plus size={18} weight="bold" />
        </Button>
      </div>
      )}
    </div>
  );
}

function MaterialList({ project, setMaterialStatus, onChange, navigate, onCheckShoppingList }) {
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestingId, setSuggestingId] = useState(null);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      setBusy(true);
      await api.post(`/projects/${project.id}/materials`, {
        name: newName.trim(),
        quantity: newQty.trim(),
        status: "need_to_buy",
      });
      setNewName(""); setNewQty("");
      onChange();
    } finally {
      setBusy(false);
    }
  };

  const getAiRec = async (materialId) => {
    try {
      setSuggestingId(materialId);
      await api.post(`/projects/materials/${materialId}/suggest`);
      toast.success("Got an AI recommendation");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't get a recommendation");
    } finally {
      setSuggestingId(null);
    }
  };

  const cycleStatus = (cur) => {
    const order = ["need_to_buy", "bought", "have", "not_needed"];
    return order[(order.indexOf(cur) + 1) % order.length];
  };

  const needCount = (project.materials || []).filter((m) => m.status === "need_to_buy").length;
  const requiredOutstanding = (project.materials || []).filter(
    (m) => (m.status === "need_to_buy" || m.status === "unsure") && m.required === true,
  );
  const showAwaitingBanner = requiredOutstanding.length > 0;

  return (
    <div className="space-y-2" data-testid="materials-list">
      {showAwaitingBanner && (
        <div
          data-testid="awaiting-materials-banner"
          className="bg-masking-50 border-2 border-masking rounded-xl p-3 flex items-start gap-2"
        >
          <Warning size={18} weight="fill" className="text-masking-700 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="font-bold text-sm text-timber-900">
              {requiredOutstanding.length} required {requiredOutstanding.length === 1 ? "item" : "items"} to buy before work begins
            </p>
            <p className="text-xs text-timber-700 mt-0.5">
              {`Tap a row below to mark it as bought once it's in your hand.`}
            </p>
          </div>
        </div>
      )}
      <button
        onClick={onCheckShoppingList}
        data-testid="check-shopping-list-btn"
        className="w-full tactile-card p-3 flex items-center justify-between btn-press hover:shadow-tactile-orange border-denim"
      >
        <div className="flex items-center gap-2">
          <MagicWand size={20} weight="duotone" className="text-denim" />
          <div className="text-left">
            <p className="font-bold text-timber-900">Final materials review</p>
            <p className="text-[11px] text-timber-700">AI flags likely-missing items before your store run</p>
          </div>
        </div>
        <Sparkle size={16} className="text-denim" />
      </button>
      {needCount > 0 && (
        <button
          onClick={() => navigate(`/projects/${project.id}/shop`)}
          data-testid="open-shop-btn"
          className="w-full tactile-card p-3 flex items-center justify-between btn-press hover:shadow-tactile-orange"
        >
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} className="text-masking" />
            <span className="font-bold text-timber-900">Shop list ({needCount})</span>
          </div>
          <span className="text-masking font-bold text-sm">View →</span>
        </button>
      )}
      {project.materials?.length === 0 && (
        <p className="text-sm text-timber-500">No materials yet.</p>
      )}
      {project.materials?.map((m) => {
        const hasSuggestion = !!m.purchase_suggestion && (
          m.purchase_suggestion.recommended?.product_name ||
          m.purchase_suggestion.product_type ||
          (m.purchase_suggestion.retailers?.length > 0)
        );
        return (
          <div key={m.id} data-testid={`material-row-${m.id}`} className={`tactile-card p-3 ${m.required && (m.status === "need_to_buy" || m.status === "unsure") ? "border-masking" : ""}`}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <p className={`font-semibold ${
                  m.status === "bought" || m.status === "have" ? "text-timber-900" :
                  m.status === "not_needed" ? "line-through text-timber-500" : "text-timber-900"
                }`}>
                  {m.name}{m.quantity && <span className="text-timber-500 font-normal"> · {m.quantity}</span>}
                  {m.required && (m.status === "need_to_buy" || m.status === "unsure") && (
                    <span data-testid={`material-required-badge-${m.id}`} className="ml-2 inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider text-masking-700 bg-masking-50 border border-masking rounded-full px-1.5 py-0.5 align-middle">
                      Required
                    </span>
                  )}
                </p>
                {m.explanation && m.status === "need_to_buy" && (
                  <p className="text-[11px] text-timber-700 italic mt-0.5">{m.explanation}</p>
                )}
                {m.purchase_suggestion?.category && (
                  <p className="text-[11px] text-timber-500 font-mono uppercase tracking-wider mt-0.5">
                    {m.purchase_suggestion.category}
                  </p>
                )}
              </div>
              <button
                data-testid={`material-cycle-${m.id}`}
                onClick={() => setMaterialStatus(m.id, cycleStatus(m.status))}
              >
                <StatusPill status={m.status} />
              </button>
            </div>
            {hasSuggestion && m.status === "need_to_buy" && (
              <div className="mt-3">
                <PurchaseRecCard suggestion={m.purchase_suggestion} compact />
              </div>
            )}
            {!hasSuggestion && m.status === "need_to_buy" && (
              <button
                data-testid={`material-suggest-${m.id}`}
                onClick={() => getAiRec(m.id)}
                disabled={suggestingId === m.id}
                className="mt-3 w-full text-xs font-bold text-denim border-2 border-dashed border-stone_border bg-kraft rounded-lg px-2 py-2 flex items-center justify-center gap-1.5 hover:border-denim btn-press disabled:opacity-60"
              >
                <Sparkle size={14} weight={suggestingId === m.id ? "regular" : "fill"} />
                {suggestingId === m.id ? "Asking the copilot…" : "Get AI recommendation"}
              </button>
            )}
          </div>
        );
      })}
      <div className="flex gap-2 pt-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Material name"
          data-testid="material-new-name"
          className="min-h-[52px] flex-1 bg-chalk border-2 border-stone_border rounded-xl"
        />
        <Input
          value={newQty}
          onChange={(e) => setNewQty(e.target.value)}
          placeholder="Qty"
          data-testid="material-new-qty"
          className="min-h-[52px] w-20 bg-chalk border-2 border-stone_border rounded-xl"
        />
        <Button onClick={add} disabled={busy} className="bg-timber-900 text-masking min-h-[52px]" data-testid="material-add-btn">
          <Plus size={18} weight="bold" />
        </Button>
      </div>
    </div>
  );
}

const UPDATE_LABELS = {
  ran_out: "Ran out",
  broken_tool: "Tool broke",
  new_issue: "New issue",
  extra_material: "Extra material",
  step_blocked: "Step blocked",
  revisit_step: "Revisited step",
  note: "Note",
  ai_assist: "AI suggestion applied",
};

const HELP_LABELS = {
  need_help: "Need help",
  check_my_work: "Check my work",
  check_shopping_list: "Shopping list review",
};

function HistoryTimeline({ updates, helpRecs, project }) {
  const [filter, setFilter] = useState("all");
  const stepById = Object.fromEntries((project.steps || []).map((s) => [s.id, s]));
  const matById = Object.fromEntries((project.materials || []).map((m) => [m.id, m]));

  const merged = [
    ...updates.map((u) => ({ kind_class: "update", ...u })),
    ...helpRecs.map((h) => ({ kind_class: "help", ...h })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  const filtered = filter === "all" ? merged : merged.filter((x) => x.kind_class === filter);

  if (merged.length === 0) {
    return (
      <div className="tactile-card p-6 text-center" data-testid="history-empty">
        <Clock size={32} weight="duotone" className="mx-auto text-timber-500" />
        <p className="text-timber-700 mt-2 text-sm">
          Nothing in your project history yet. Mid-project notes and AI help will appear here.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="history-timeline">
      <div className="flex gap-1.5 mb-3" data-testid="history-filters">
        {[
          { k: "all", label: `All (${merged.length})` },
          { k: "update", label: `Updates (${updates.length})` },
          { k: "help", label: `Help (${helpRecs.length})` },
        ].map(({ k, label }) => (
          <button
            key={k}
            data-testid={`history-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border-2 ${
              filter === k
                ? "bg-timber-900 border-timber-900 text-masking"
                : "bg-chalk border-stone_border text-timber-700"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <ul className="space-y-3">
        {filtered.map((item) =>
          item.kind_class === "update" ? (
            <UpdateRow key={`u-${item.id}`} u={item} stepById={stepById} matById={matById} />
          ) : (
            <HelpRow key={`h-${item.id}`} h={item} stepById={stepById} />
          )
        )}
      </ul>
    </div>
  );
}

function UpdateRow({ u, stepById, matById }) {
  return (
    <li data-testid={`update-${u.id}`} className="tactile-card p-4">
      <div className="flex items-center justify-between">
        <span className="label-cap">{UPDATE_LABELS[u.kind] || u.kind}</span>
        <span className="text-[11px] text-timber-500 font-mono">
          {new Date(u.created_at).toLocaleDateString()} · {new Date(u.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
      {u.note && <p className="text-sm text-timber-900 mt-1">{u.note}</p>}
      {u.added_material_ids?.length > 0 && (
        <div className="mt-2 text-xs text-timber-700">
          <span className="font-bold">Added materials:</span>{" "}
          {u.added_material_ids.map((mid) => matById[mid]?.name).filter(Boolean).join(", ")}
        </div>
      )}
      {u.added_step_ids?.length > 0 && (
        <div className="mt-1 text-xs text-timber-700">
          <span className="font-bold">Added steps:</span>{" "}
          {u.added_step_ids.map((sid) => stepById[sid]?.title).filter(Boolean).join(", ")}
        </div>
      )}
      {u.affected_step_id && stepById[u.affected_step_id] && (
        <div className="mt-1 text-xs text-timber-700">
          <span className="font-bold">Affected step:</span> {stepById[u.affected_step_id].title}
        </div>
      )}
      {u.photo_id && (
        <img src={fileUrl(u.photo_id)} alt="" className="mt-2 rounded-lg border-2 border-stone_border max-h-48" />
      )}
    </li>
  );
}

function HelpRow({ h, stepById }) {
  const ai = h.ai_response || {};
  return (
    <li data-testid={`help-rec-${h.id}`} className="tactile-card p-4 border-denim">
      <div className="flex items-center justify-between">
        <span className="label-cap text-denim flex items-center gap-1">
          <Lifebuoy size={12} weight="fill" /> {HELP_LABELS[h.kind] || h.kind}
        </span>
        <span className="text-[11px] text-timber-500 font-mono">
          {new Date(h.created_at).toLocaleDateString()} · {new Date(h.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
      {h.step_id && stepById[h.step_id] && (
        <p className="text-[11px] text-timber-700 mt-0.5">on step: {stepById[h.step_id].title}</p>
      )}
      {h.user_question && (
        <p className="text-xs text-timber-700 italic mt-1">{`"${h.user_question}"`}</p>
      )}
      {ai.recommended_action && (
        <p className="font-heading text-sm font-bold text-timber-900 mt-1.5 leading-snug">
          {ai.recommended_action}
        </p>
      )}
      {ai.observation && (
        <p className="text-xs text-timber-700 mt-1">{ai.observation}</p>
      )}
      {h.photo_id && (
        <img src={fileUrl(h.photo_id)} alt="" className="mt-2 rounded-lg border-2 border-stone_border max-h-48" />
      )}
      {h.applied && (
        <p className="text-[11px] text-ok font-bold mt-2 uppercase tracking-wider">
          Applied · {h.applied_step_ids?.length || 0} step(s), {h.applied_material_ids?.length || 0} material(s)
        </p>
      )}
    </li>
  );
}
